import React from 'react';
import { Link } from 'react-router-dom';

const mainColor = '#38b2ac';
const accentColor = '#48bb78';
const glassBg = 'rgba(255,255,255,0.55)';
const glassShadow = '0 8px 32px rgba(60, 60, 120, 0.18)';
const vibrantGradient = 'linear-gradient(120deg, #38b2ac 0%, #667eea 100%)';
const statsGlow = '0 0 16px 2px #38b2ac33, 0 2px 8px #48bb7833';
const fontFamily = 'Inter, Segoe UI, Arial, sans-serif';

const LandingPage = () => {
  return (
    <div style={{ minHeight: '100vh', background: vibrantGradient, fontFamily }}>
      {/* Animated Particles */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 20% 30%, #38b2ac22 0%, transparent 70%), radial-gradient(circle at 80% 70%, #667eea22 0%, transparent 70%)' }} />
      
      {/* Sticky Glass Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(255,255,255,0.75)', boxShadow: '0 2px 16px #38b2ac22', padding: 0, borderBottom: '1.5px solid #e0e7ff', backdropFilter: 'blur(12px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 34, color: mainColor, fontWeight: 900, filter: 'drop-shadow(0 2px 8px #38b2ac44)' }}>📝</span>
            <span style={{ fontWeight: 900, fontSize: '1.7rem', color: '#2d3748', letterSpacing: '-1px', textShadow: '0 2px 8px #38b2ac11' }}>Scan-it-Now</span>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            {/* Admin Login button removed */}
          </div>
        </div>
      </header>

      <main style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <section style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <h1 style={{ fontSize: '3.5rem', fontWeight: 900, color: '#2d3748', marginBottom: 24, letterSpacing: '-1px', lineHeight: 1.2 }}>
              AI-Powered Document<br />Similarity Checker
            </h1>
            <p style={{ fontSize: '1.3rem', color: '#4a5568', marginBottom: 40, lineHeight: 1.6 }}>
              Upload your documents and get instant AI-powered similarity analysis.<br />
              Perfect for students, researchers, and content creators.
            </p>
            <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/scan" style={{ padding: '16px 36px', background: vibrantGradient, color: 'white', border: 'none', borderRadius: 12, fontWeight: 900, fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac22', display: 'flex', alignItems: 'center', gap: 12, letterSpacing: '0.5px', transition: 'background 0.2s', textDecoration: 'none' }}>
                <span style={{ fontSize: 24 }}>🚀</span> Start Scanning
              </Link>
              <a href="#features" style={{ padding: '16px 36px', background: 'rgba(255,255,255,0.9)', color: '#2d3748', border: '1.5px solid #e0e7ff', borderRadius: 12, fontWeight: 900, fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac11', display: 'flex', alignItems: 'center', gap: 12, letterSpacing: '0.5px', transition: 'all 0.2s', textDecoration: 'none' }}>
                <span style={{ fontSize: 24 }}>✨</span> Learn More
              </a>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" style={{ padding: '80px 20px', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: '#2d3748', marginBottom: 60, textAlign: 'center', letterSpacing: '-1px' }}>Why Choose Us?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32 }}>
              {[
                {
                  icon: '🤖',
                  title: 'AI-Powered Analysis',
                  description: 'Advanced AI algorithms analyze your documents for similarity and provide detailed reports.',
                },
                {
                  icon: '⚡',
                  title: 'Instant Results',
                  description: 'Get your similarity analysis results quickly and efficiently.',
                },
                {
                  icon: '🔒',
                  title: 'Secure & Private',
                  description: 'Your documents are handled with utmost security and privacy.',
                },
                {
                  icon: '📊',
                  title: 'Detailed Reports',
                  description: 'Comprehensive reports with similarity scores and highlighted matches.',
                },
                {
                  icon: '💳',
                  title: 'Affordable Pricing',
                  description: 'Only 10 Pesos per file. No hidden charges or subscriptions.',
                },
                {
                  icon: '📱',
                  title: 'Mobile Friendly',
                  description: 'Access our service from any device, anywhere, anytime.',
                },
              ].map((feature, index) => (
                <div
                  key={feature.title}
                  style={{
                    background: glassBg,
                    padding: 32,
                    borderRadius: 22,
                    boxShadow: glassShadow,
                    backdropFilter: 'blur(10px)',
                    border: '1.5px solid #e0e7ff',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    animation: `fadeIn 0.7s ${0.1 * index}s both`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ fontSize: 48, marginBottom: 20, color: mainColor, filter: 'drop-shadow(0 2px 8px #38b2ac33)', animation: 'iconPulse 1.5s infinite alternate' }}>{feature.icon}</div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#2d3748', marginBottom: 12 }}>{feature.title}</h3>
                  <p style={{ color: '#4a5568', fontSize: '1.1rem', lineHeight: 1.6 }}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: '#2d3748', marginBottom: 24, letterSpacing: '-1px' }}>Ready to Get Started?</h2>
            <p style={{ fontSize: '1.2rem', color: '#4a5568', marginBottom: 40, lineHeight: 1.6 }}>
              Upload your documents now and get instant AI-powered similarity analysis.<br />
              Only 10 Pesos per file!
            </p>
            <Link to="/scan" style={{ padding: '16px 36px', background: vibrantGradient, color: 'white', border: 'none', borderRadius: 12, fontWeight: 900, fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac22', display: 'inline-flex', alignItems: 'center', gap: 12, letterSpacing: '0.5px', transition: 'background 0.2s', textDecoration: 'none' }}>
              <span style={{ fontSize: 24 }}>🚀</span> Start Scanning Now
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ background: 'rgba(255,255,255,0.75)', padding: '40px 20px', textAlign: 'center', borderTop: '1.5px solid #e0e7ff', backdropFilter: 'blur(12px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 34, color: mainColor, fontWeight: 900, filter: 'drop-shadow(0 2px 8px #38b2ac44)' }}>📝</span>
            <span style={{ fontWeight: 900, fontSize: '1.7rem', color: '#2d3748', letterSpacing: '-1px', textShadow: '0 2px 8px #38b2ac11' }}>Scan-it-Now</span>
          </div>
          <p style={{ color: '#4a5568', fontSize: '1.1rem', marginBottom: 20 }}>AI-Powered Document Similarity Checker</p>
          <div style={{ color: '#718096', fontSize: '1rem' }}>&copy; {new Date().getFullYear()} Scan-it-Now. All rights reserved.</div>
        </div>
      </footer>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes iconPulse {
          from { filter: drop-shadow(0 2px 8px #38b2ac33); }
          to { filter: drop-shadow(0 6px 16px #48bb7833); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage; 